import { useEffect, useState, type ReactNode } from 'react';
import { api, type Kontrahent } from '../api';
import { idz } from '../router';
import { PoleTekstowe, Przycisk } from './ui';

/**
 * Wybór kontrahenta przez wyszukiwanie, nie przez listę rozwijaną: kontrahentów
 * są setki, a docelowo przyjdą z Subiekta w liczbie, której nikt nie przewinie.
 *
 * Komponent mieszkał wcześniej osobno w formularzu montażu i w formularzu karty
 * urządzenia — dwie identyczne kopie. Trzecie miejsce (zakładanie karty w zgłoszeniu)
 * przeważyło: jedno zachowanie, jedno miejsce do poprawiania.
 */
export function WyborKontrahenta({
  wybrany,
  onWybor,
  etykieta = 'Klient',
  /** Co pokazać, gdy szukanie nic nie daje. Domyślnie odsyła do listy kontrahentów. */
  akcjaBraku,
}: {
  wybrany: Kontrahent | null;
  onWybor: (kontrahent: Kontrahent | null) => void;
  etykieta?: string;
  akcjaBraku?: ReactNode;
}) {
  const [szukane, setSzukane] = useState('');
  const [wyniki, setWyniki] = useState<Kontrahent[]>([]);

  useEffect(() => {
    if (wybrany) return;
    const fraza = szukane.trim();

    // Puste pole nie jest zapytaniem. Bez tego lista ośmiu przypadkowych kontrahentów
    // rozpychała formularz i zasłaniała pola, o które tu chodzi.
    if (fraza.length < 2) {
      setWyniki([]);
      return;
    }

    const zegar = window.setTimeout(() => {
      api
        .kontrahenci({ q: fraza, limit: 8 })
        .then((wynik) => setWyniki(wynik.kontrahenci))
        .catch(() => setWyniki([]));
    }, 250);

    return () => window.clearTimeout(zegar);
  }, [szukane, wybrany]);

  if (wybrany) {
    return (
      <div>
        <span className="t-label mb-5 block text-n-700">{etykieta}</span>
        <div className="flex min-h-44 flex-wrap items-center gap-13 border border-n-300 bg-surface px-13 py-5">
          <span className="t-body-strong mr-auto text-n-900">{wybrany.nazwa}</span>
          <Przycisk type="button" onClick={() => onWybor(null)}>
            Zmień
          </Przycisk>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PoleTekstowe
        etykieta={etykieta}
        podpowiedz="szukaj po nazwie albo NIP"
        value={szukane}
        onChange={(zdarzenie) => setSzukane(zdarzenie.target.value)}
        autoComplete="off"
      />

      {wyniki.length > 0 && (
        <div className="mt-5 border border-n-100 bg-surface">
          {wyniki.map((kontrahent) => (
            <button
              key={kontrahent.id}
              type="button"
              onClick={() => onWybor(kontrahent)}
              className="flex w-full flex-wrap items-baseline gap-x-13 border-0 border-b border-n-100 bg-surface px-13 text-left last:border-b-0 hover:bg-n-50"
            >
              <span className="t-body mr-auto text-n-900">{kontrahent.nazwa}</span>
              <span className="t-mono-data text-n-500">{kontrahent.nip ?? ''}</span>
            </button>
          ))}
        </div>
      )}

      {wyniki.length === 0 && szukane.trim() !== '' && (
        <div className="mt-9 flex flex-wrap items-center gap-13">
          <span className="t-small text-n-600">Nic nie pasuje.</span>
          {akcjaBraku ?? (
            <Przycisk type="button" onClick={() => idz('/kontrahenci')}>
              Dodaj kontrahenta
            </Przycisk>
          )}
        </div>
      )}
    </div>
  );
}
